import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  HelpCircle,
  Plus,
  Pencil,
  Trash2,
  Tag,
  BarChart3,
  Clock,
  CheckCircle2,
  Upload,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  PageHeader,
  EmptyState,
  SearchInput,
  ConfirmModal,
  AppButton,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./QuizQuestionsV2.scss";

const QUESTION_TYPE_LABELS = {
  single_choice: "Single Choice",
  multi_select: "Multi Select",
  true_false: "True / False",
  text_answer: "Text Answer",
  fill_blank: "Fill in Blank",
  image_mcq: "Image MCQ",
  code_conceptual: "Code Conceptual",
};
const DIFFICULTY_LABELS = { easy: "Easy", medium: "Medium", hard: "Hard" };
const PAGE_SIZE = 20;

function getPaginationNumbers(current, total) {
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("...");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push("...");
  if (total > 1) pages.push(total);
  return pages;
}

// ─── Small presentational bits ──────────────────────────────────────────
const StatTile = ({ label, value, icon: Icon, tone = "blue" }) => (
  <div className={`qq-stat qq-stat--${tone}`}>
    <div className="qq-stat-head">
      <span className="qq-stat-label">{label}</span>
      {Icon && <Icon size={16} className="qq-stat-icon" />}
    </div>
    <div className="qq-stat-value">{value}</div>
  </div>
);

const DifficultyDot = ({ level }) => (
  <span className={`qq-diff qq-diff--${level}`}>
    <span className="qq-diff-dot" />
    {DIFFICULTY_LABELS[level] || level}
  </span>
);

// ─── Main page ──────────────────────────────────────────────────────────
const QuizQuestionsPage = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    category: "",
    difficulty: "",
    questionType: "",
  });
  const [sort, setSort] = useState("newest");
  const [filterData, setFilterData] = useState({
    categories: [],
    difficulties: {},
    questionTypes: {},
    totalQuestions: 0,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const fetchFilters = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/quiz-questions/filters");
      setFilterData(data);
    } catch {
      /* silent */
    }
  }, []);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        sort,
        ...(search && { search }),
        ...(filters.category && { category: filters.category }),
        ...(filters.difficulty && { difficulty: filters.difficulty }),
        ...(filters.questionType && { questionType: filters.questionType }),
      };
      const { data } = await apiClient.get("/quiz-questions", { params });
      setQuestions(data.questions || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      setToast({
        show: true,
        message: "Failed to load questions",
        type: "error",
      });
    }
    setLoading(false);
  }, [page, pageSize, search, filters, sort]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);
  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/quiz-questions/${deleteTarget._id}`);
      setToast({ show: true, message: "Question deleted", type: "success" });
      fetchQuestions();
      fetchFilters();
    } catch (err) {
      setToast({
        show: true,
        message: err.response?.data?.message || "Failed to delete",
        type: "error",
      });
    }
    setDeleteTarget(null);
  };

  const startIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, total);

  const topCategories = useMemo(
    () => (filterData.categories || []).filter((c) => !c.parentCategory),
    [filterData.categories]
  );

  const hasActiveFilters =
    !!search ||
    !!filters.category ||
    !!filters.difficulty ||
    !!filters.questionType;

  const clearFilters = () => {
    setSearch("");
    setFilters({ category: "", difficulty: "", questionType: "" });
    setSort("newest");
    setPage(1);
  };

  const headerActions = (
    <div className="qq-header-actions">
      <AppButton
        variant="outline"
        size="sm"
        onClick={() => navigate("/quiz-bank/bulk-upload")}
      >
        <Upload size={14} /> <span>Bulk Upload</span>
      </AppButton>
      <AppButton
        variant="default"
        size="sm"
        onClick={() => navigate("/quiz-bank/add")}
      >
        <Plus size={14} /> <span>Add Question</span>
      </AppButton>
    </div>
  );

  const pageNumbers = getPaginationNumbers(page, totalPages);

  return (
    <div className="quiz-questions-v2">
      <PageHeader
        backButton
        icon={HelpCircle}
        title="Question Bank"
        description={`${filterData.totalQuestions || 0} questions across all categories`}
        actions={headerActions}
      />

      {/* ─── Stat tiles ───────────────────────────────────────────────── */}
      <div className="qq-stats">
        <StatTile
          label="Total"
          value={filterData.totalQuestions || 0}
          icon={HelpCircle}
          tone="blue"
        />
        <StatTile
          label="Easy"
          value={filterData.difficulties?.easy || 0}
          icon={CheckCircle2}
          tone="green"
        />
        <StatTile
          label="Medium"
          value={filterData.difficulties?.medium || 0}
          icon={Clock}
          tone="orange"
        />
        <StatTile
          label="Hard"
          value={filterData.difficulties?.hard || 0}
          icon={BarChart3}
          tone="pink"
        />
      </div>

      {/* ─── Toolbar ─────────────────────────────────────────────────── */}
      <div className="qq-toolbar">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder="Search questions..."
          className="qq-toolbar-search"
        />
        <div className="qq-toolbar-filters">
          <select
            className="qq-select"
            value={filters.category}
            onChange={(e) => handleFilterChange("category", e.target.value)}
          >
            <option value="">All Categories</option>
            {topCategories.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            className="qq-select"
            value={filters.difficulty}
            onChange={(e) => handleFilterChange("difficulty", e.target.value)}
          >
            <option value="">All Difficulties</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
          <select
            className="qq-select"
            value={filters.questionType}
            onChange={(e) => handleFilterChange("questionType", e.target.value)}
          >
            <option value="">All Types</option>
            {Object.entries(QUESTION_TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <select
            className="qq-select"
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              setPage(1);
            }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="easy-first">Easy First</option>
            <option value="hard-first">Hard First</option>
            <option value="most-attempted">Most Attempted</option>
          </select>
          {hasActiveFilters && (
            <AppButton variant="ghost" size="sm" onClick={clearFilters}>
              Clear
            </AppButton>
          )}
        </div>
      </div>

      {/* ─── Question list ───────────────────────────────────────────── */}
      {loading ? (
        <div className="qq-skeleton-list">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="qq-skeleton-card" />
          ))}
        </div>
      ) : questions.length === 0 ? (
        <EmptyState
          icon={<HelpCircle size={40} />}
          title={hasActiveFilters ? "No matches" : "No questions yet"}
          description={
            hasActiveFilters
              ? "Try adjusting your filters or clearing them."
              : "Start building your question bank by adding your first question."
          }
          action={
            hasActiveFilters ? (
              <AppButton variant="outline" onClick={clearFilters}>
                Clear filters
              </AppButton>
            ) : (
              <AppButton
                variant="default"
                onClick={() => navigate("/quiz-bank/add")}
              >
                <Plus size={14} /> <span>Add Question</span>
              </AppButton>
            )
          }
        />
      ) : (
        <>
          <div className="qq-list">
            {questions.map((q) => (
              <article
                className="qq-card"
                key={q._id}
                onClick={() => navigate(`/quiz-bank/edit/${q._id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") navigate(`/quiz-bank/edit/${q._id}`);
                }}
                tabIndex={0}
                role="button"
              >
                <div className="qq-card-body">
                  <div className="qq-card-meta-top">
                    <span className="qq-chip qq-chip--type">
                      {QUESTION_TYPE_LABELS[q.questionType] || q.questionType}
                    </span>
                    {q.difficulty && <DifficultyDot level={q.difficulty} />}
                    {q.category?.name && (
                      <span className="qq-card-path">
                        <Tag size={12} /> {q.category.name}
                        {q.subcategory?.name && ` › ${q.subcategory.name}`}
                      </span>
                    )}
                  </div>

                  <div className="qq-card-text">{q.questionText}</div>

                  <div className="qq-card-footer">
                    <span className="qq-meta-item">
                      <BarChart3 size={13} /> {q.marks} mark
                      {q.marks !== 1 ? "s" : ""}
                    </span>
                    {q.negativeMarks > 0 && (
                      <span className="qq-meta-item qq-meta-item--danger">
                        −{q.negativeMarks} neg
                      </span>
                    )}
                    <span className="qq-meta-item">
                      <Clock size={13} /> {q.estimatedTime}s
                    </span>
                    {q.totalAttempts > 0 && (
                      <span className="qq-meta-item">
                        <CheckCircle2 size={13} />{" "}
                        {q.correctAttempts}/{q.totalAttempts} (
                        {Math.round((q.correctAttempts / q.totalAttempts) * 100)}%)
                      </span>
                    )}
                    {q.tags?.length > 0 && (
                      <div className="qq-tag-list">
                        {q.tags.slice(0, 4).map((tag) => (
                          <span className="qq-chip qq-chip--tag" key={tag}>
                            {tag}
                          </span>
                        ))}
                        {q.tags.length > 4 && (
                          <span className="qq-chip qq-chip--tag-more">
                            +{q.tags.length - 4}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className="qq-card-actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AppButton
                    variant="ghost"
                    size="icon"
                    title="Edit"
                    aria-label="Edit"
                    onClick={() => navigate(`/quiz-bank/edit/${q._id}`)}
                  >
                    <Pencil size={14} />
                  </AppButton>
                  <AppButton
                    variant="ghost"
                    size="icon"
                    title="Delete"
                    aria-label="Delete"
                    onClick={() => setDeleteTarget(q)}
                  >
                    <Trash2 size={14} />
                  </AppButton>
                </div>
              </article>
            ))}
          </div>

          {/* ─── Pagination ─────────────────────────────────────────── */}
          <div className="qq-pagination">
            <div className="qq-pagination-left">
              <span className="qq-pagination-info">
                {total === 0
                  ? "No records"
                  : `Showing ${startIdx}–${endIdx} of ${total}`}
              </span>
              <select
                className="qq-select qq-select--compact"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 20, 30, 50].map((sz) => (
                  <option key={sz} value={sz}>
                    {sz} / page
                  </option>
                ))}
              </select>
            </div>

            {totalPages > 1 && (
              <nav className="qq-pager" aria-label="Pagination">
                <button
                  type="button"
                  className="qq-pager-btn"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  aria-label="First page"
                >
                  <ChevronsLeft size={14} />
                </button>
                <button
                  type="button"
                  className="qq-pager-btn"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={14} />
                </button>
                {pageNumbers.map((num, idx) =>
                  num === "..." ? (
                    <span
                      key={`e-${idx}`}
                      className="qq-pager-ellipsis"
                      aria-hidden
                    >
                      …
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={`p-${num}`}
                      className={`qq-pager-btn ${num === page ? "qq-pager-btn--active" : ""}`}
                      onClick={() => setPage(num)}
                      aria-current={num === page ? "page" : undefined}
                    >
                      {num}
                    </button>
                  )
                )}
                <button
                  type="button"
                  className="qq-pager-btn"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight size={14} />
                </button>
                <button
                  type="button"
                  className="qq-pager-btn"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  aria-label="Last page"
                >
                  <ChevronsRight size={14} />
                </button>
              </nav>
            )}
          </div>
        </>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete question?"
        message={
          deleteTarget ? (
            <>
              <p className="mb-2">
                This will permanently delete the question:
              </p>
              <p className="mb-0">
                <strong>"{deleteTarget.questionText?.slice(0, 110)}
                {deleteTarget.questionText?.length > 110 ? "…" : ""}"</strong>
              </p>
            </>
          ) : (
            "This will permanently delete the question."
          )
        }
        confirmText="Delete"
        variant="danger"
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default QuizQuestionsPage;
